import { GoogleLogo } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { env } from '@/lib/config/env'

import { requestGoogleIdToken } from '../google'
import { requestNativeGoogleIdToken, usesNativeGoogleAuth } from '../google-native'
import { Notice } from './notice'

interface GoogleButtonProps {
  disabled?: boolean
  onCredential: (idToken: string) => void
}

/**
 * Google sign-in entry point. Provider credentials are optional until the
 * Google developer account exists (D-044): when the build flag or client ID is
 * missing the button is disabled with a user-facing explanation instead of
 * failing at request time.
 *
 * The mobile shells request the ID token through the native Google plugin
 * (D-076); browsers keep Google Identity Services.
 */
export function GoogleButton({ disabled = false, onCredential }: GoogleButtonProps) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)

  const configured =
    env.providers.google && env.providers.googleClientId.trim().length > 0
  const unavailable = !configured
  const isDisabled = disabled || busy || unavailable

  async function handleClick() {
    setErrorKey(null)
    setBusy(true)
    try {
      const idToken = usesNativeGoogleAuth()
        ? await requestNativeGoogleIdToken(env.providers.googleClientId)
        : await requestGoogleIdToken(env.providers.googleClientId)
      onCredential(idToken)
    } catch (error) {
      // Closing the native account picker is a normal choice, not an error.
      if (String(error).toLowerCase().includes('cancel')) {
        return
      }
      setErrorKey('auth.google.unavailable')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <button
        type="button"
        className="btn-secondary w-full rounded-[18px] bg-white"
        onClick={() => void handleClick()}
        disabled={isDisabled}
        aria-disabled={isDisabled}
      >
        <GoogleLogo size={24} weight="bold" aria-hidden="true" />
        {busy ? t('auth.google.connecting') : t('auth.login.google')}
      </button>
      {unavailable ? (
        <p className="mt-1.5 text-xs leading-snug text-muted">
          {t('errors.providerNotConfigured')}
        </p>
      ) : null}
      {errorKey ? (
        <Notice tone="error" className="mt-3">
          {t(errorKey)}
        </Notice>
      ) : null}
    </div>
  )
}
